import { useEffect, useCallback, useState } from 'react';
import { AddressForm } from './AddressForm';
import { ComponentConfig } from '@measured/puck';
import { Controller, useFormContext } from 'react-hook-form';
import useDebounce from '../../../page-builder/hooks/useDebounce';
import {
  defaultBorderProps,
  getIsViewIsEdit,
  toolTipInfo,
} from '../../../page-builder/utils/constants';
import { FormData } from '../../../page-builder/interface/checkoutInterface';
import { ShippingMethod } from '../../../page-builder/interface/checkoutInterface';
import {
  setApiLoading,
  setAvailableShippingMethods,
  setIsSetShippingApiLoading,
} from '../../../page-builder/redux/slices/checkoutSlice';
import {
  setCheckoutErrorMessage,
  setIsValidShippingForm,
} from '../../../page-builder/redux/slices/commonSlice';
import checkoutService from '../../api/checkoutService';
import {
  marginFields,
  MarginProps,
} from '../../../page-builder/puck/reusable-props/margin';
import {
  ReduxDispatchProps,
  ReduxSelectorProps,
} from '../../services/constants';
import CustomTooltip from '../../../components/common/tooltip';
import { EventPublisher } from '../../utils/publishEvent';
import { IAddressOptions, IFieldOption } from '../../interface/formInterface';
import {
  fieldHeightProps,
  inputFieldHeightProps,
} from '../../puck/reusable-props/inputfield-height';
import {
  getBorderProps,
  getcurrentDeviceType,
  getMarginStyles,
  getPaddingStyles,
  getPhoneCode,
  getQuillEditorText,
} from '../../utils/helper';
import { FieldLabel } from '@measured/puck';
import AddressHelper from './AddressHelper';
import {
  paddingFields,
  paddingProps,
  PaddingProps,
} from '../../puck/reusable-props/padding';
import Border, {
  borderProps,
  IBorderValueProps,
} from '../../puck/reusable-props/Border';
import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import ColorPicker from '../../puck/reusable-props/ColorPicker';
import WithAccordion from '../customComponents/CustomAccordion';
import { QuillTextEditor } from '../../puck/reusable-props/quill-editor';
import 'react-quill-new/dist/quill.snow.css';
import '../../components/text-editor/style.scss';
import { setSalesTax } from '../../redux/slices/cartSlice';
import { marginProps } from '../../puck/reusable-props/NewMargin';
import Select from '../../../components/common/select';
import Toggle from '../../../components/common/toggleSwitchCustom';
import NewToggle from '../../../components/common/toggleButton';
import { Switch, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { outlineProps } from '../../puck/reusable-props/Outline';
import { v4 as uuidv4 } from 'uuid';

// Type declaration for global window property
interface ShippingData {
  Address1: string;
  Address2: string;
  City: string;
  CountryCode: string;
  FirstName: string;
  LastName: string;
  Organization: string;
  ProvinceCode: string;
  Zip: string;
  Email: string;
  PhoneNumber: string;
}

declare global {
  interface Window {
    setGlobalShippingData?: (_data: ShippingData) => void;
  }
}

export type option = IFieldOption & {
  Phone?: IAddressOptions;
};

export interface IMaintainRowsProps {
  deviceType: string;
  firstLastName: {
    [key: string]: boolean;
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
  };
  countryProvince: {
    [key: string]: boolean;
    mobile: boolean;
    tablet: boolean;
    desktop: boolean;
  };
}

export type ShippingForm = {
  option: option;
  billinForm?: option;
  customCheckboxText: string;
  maintainRows: IMaintainRowsProps;
  customCheckbox?: {
    checkboxColor?: string;
    checkedBorderColor?: string;
    uncheckedBorderColor?: string;
    checkboxshape?: string;
    checkboxSize?: string;
    enrollmentStyle?: string;
    checkMarkColor?: string;
    checkedToggleColor?: string;
    uncheckedToggleColor?: string;
    toggleSize?: string;
    checkMarkSize?: string;
  };
  isShowBillingAddress?: boolean;
  border: IBorderValueProps;
} & MarginProps &
  fieldHeightProps &
  PaddingProps;

export const ShippingForm = ({
  useAppDispatch,
  useAppSelector,
}: ReduxDispatchProps & ReduxSelectorProps): ComponentConfig<ShippingForm> => {
  return {
    label: (
      <CustomTooltip title="ShippingForm">
        <span>Shipping Form</span>
      </CustomTooltip>
    ) as React.ReactNode as string,
    fields: {
      ...marginFields,
      ...paddingFields,
      maintainRows: {
        type: 'custom',
        label: 'Maintain Rows',
        render: ({ value, onChange }) => {
          return (
            <WithAccordion title="Maintain Single Row">
              <NewToggle<IMaintainRowsProps>
                value={value}
                onChange={(newValue: IMaintainRowsProps) =>
                  onChange({ ...value, ...newValue })
                }
                heading={{ label: 'Device', value: 'device' }}
                options={[
                  { label: 'Desktop', value: 'desktop' },
                  { label: 'Tablet', value: 'tablet' },
                  { label: 'Mobile', value: 'mobile' },
                ]}
                propName="deviceType"
              />
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold">First And Last Name</p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title={'Toggle to view First And Last Name in a single row'}
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-1 w-1" />
                  </Tooltip>
                  <Switch
                    onChange={e =>
                      onChange({
                        ...value,
                        firstLastName: {
                          ...value?.firstLastName,
                          [value?.deviceType]: e.target.checked,
                        },
                      })
                    }
                    checked={value?.firstLastName?.[value?.deviceType]}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>
              <div className="flex justify-between items-center">
                <p className="text-sm font-semibold">City Country</p>
                <div className="flex gap-0.5 items-center">
                  <Tooltip
                    title={'Toggle to view City And Country in a single row'}
                    placement="top-start">
                    <InfoIcon fontSize="small" className="h-1 w-1" />
                  </Tooltip>
                  <Switch
                    onChange={e =>
                      onChange({
                        ...value,
                        countryProvince: {
                          ...value?.countryProvince,
                          [value?.deviceType]: e.target.checked,
                        },
                      })
                    }
                    checked={value?.countryProvince?.[value?.deviceType]}
                    inputProps={{ 'aria-label': 'controlled' }}
                  />
                </div>
              </div>
            </WithAccordion>
          );
        },
      },
      border: {
        type: 'custom',
        render: ({ value, onChange }) => (
          <Border value={value} onChange={onChange} title="Border" />
        ),
      },
      option: {
        type: 'custom',
        label: 'Options',
        render: ({ value, onChange }) => {
          return (
            <AddressHelper
              value={value}
              onChange={onChange}
              name={`Shipping Form`}
            />
          );
        },
      },
      ...inputFieldHeightProps(),
      isShowBillingAddress: {
        type: 'radio',
        label: 'Show Billing Address',
        options: [
          { label: 'Yes', value: true },
          { label: 'No', value: false },
        ],
      },

      customCheckboxText: {
        ...QuillTextEditor('Custom Checkbox Text').content,
      },

      customCheckbox: {
        type: 'custom',
        label: 'Custom Checkbox',
        render: ({ value, onChange }) => {
          const options = [
            { label: 'Square', value: 'rounded' },
            { label: 'Circle', value: 'rounded-full' },
          ];
          return (
            <WithAccordion title="Custom Checkbox">
              <>
                <div>
                  <ColorPicker
                    value={value?.checkboxColor || '#000000'}
                    onChange={color =>
                      onChange({ ...(value || {}), checkboxColor: color })
                    }
                    label="Checkbox Color"
                    tooltip="Checkbox Color"
                  />
                </div>
                <div>
                  <ColorPicker
                    value={value?.checkedBorderColor}
                    onChange={color =>
                      onChange({ ...(value || {}), checkedBorderColor: color })
                    }
                    label="Checked Border Color"
                    tooltip="Checked Border Color"
                  />
                </div>
                <div>
                  <ColorPicker
                    value={value?.uncheckedBorderColor}
                    onChange={color =>
                      onChange({
                        ...(value || {}),
                        uncheckedBorderColor: color,
                      })
                    }
                    label="Unchecked Border Color"
                    tooltip="Unchecked Border Color"
                  />
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Shape' as keyof typeof toolTipInfo}>
                          <span>Checkbox Shape</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <div className="w-full p-2">
                      <ToggleButtonGroup
                        value={value?.checkboxshape}
                        exclusive
                        className="w-full"
                        onChange={(_e, selectValue) => {
                          selectValue &&
                            onChange({ ...value, checkboxshape: selectValue });
                        }}>
                        {options.map((option, index: number) => (
                          <ToggleButton
                            key={index}
                            size="small"
                            value={option.value}
                            disableRipple
                            aria-label={option.value}
                            className="flex-1">
                            {option.label}
                          </ToggleButton>
                        ))}
                      </ToggleButtonGroup>
                    </div>
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Checkbox Size' as keyof typeof toolTipInfo}>
                          <span>Checkbox Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkboxSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkboxSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter checkbox size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip title="EnrollementStyle">
                          <span>Enrollement Style</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                  />
                  <Select
                    value={value?.enrollmentStyle}
                    onChange={val =>
                      onChange({ ...value, enrollmentStyle: val })
                    }
                    options={[
                      {
                        label: 'Checkbox',
                        value: 'checkbox',
                      },
                      {
                        label: 'Toggle',
                        value: 'toggle',
                      },
                    ]}
                  />
                </div>
                <ColorPicker
                  value={value?.checkMarkColor || '#ffffff'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkMarkColor: color,
                    })
                  }
                  label={'Check Mark Color'}
                  tooltip={'Check Mark Color'}
                />
                <ColorPicker
                  value={value?.checkedToggleColor || '#4f46e5'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      checkedToggleColor: color,
                    })
                  }
                  label={'Checked Toggle Color'}
                  tooltip={'Checked Toggle Color'}
                />
                <ColorPicker
                  value={value?.uncheckedToggleColor || '#d1d5db'}
                  onChange={color =>
                    onChange({
                      ...(value || {}),
                      uncheckedToggleColor: color,
                    })
                  }
                  label={'Unchecked Toggle Color'}
                  tooltip={'Unchecked Toggle Color'}
                />
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Toggle Size' as keyof typeof toolTipInfo}>
                          <span>Toggle Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.toggleSize || 20}
                      onChange={e =>
                        onChange({
                          ...value,
                          toggleSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter toggle size"
                    />
                  </FieldLabel>
                </div>
                <div>
                  <FieldLabel
                    label={
                      (
                        <CustomTooltip
                          title={'Check Mark Size' as keyof typeof toolTipInfo}>
                          <span>Check Mark Size</span>
                        </CustomTooltip>
                      ) as React.ReactNode as string
                    }
                    el="div"
                    className="w-full">
                    <input
                      type="number"
                      value={value?.checkMarkSize || 16}
                      onChange={e =>
                        onChange({
                          ...value,
                          checkMarkSize: e.target.value,
                        })
                      }
                      className="w-full p-2 border border-gray-300 rounded"
                      placeholder="Enter toggle size"
                    />
                  </FieldLabel>
                </div>
              </>
            </WithAccordion>
          );
        },
      },
      billinForm: {
        type: 'custom',
        label: 'Billing Form',
        render: ({ value, onChange }) => {
          return value ? (
            <AddressHelper
              value={value}
              onChange={onChange}
              name={`Billing Form`}
            />
          ) : (
            <></>
          );
        },
      },
    },
    defaultProps: {
      margin: {
        desktop: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        tablet: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
        mobile: {
          top: '0',
          bottom: '0',
          right: '0',
          left: '0',
        },
      },
      heightField: {
        height: {
          desktop: '42',
          tablet: '42',
          mobile: '42',
        },
        rowGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        colGap: {
          mobile: '10',
          tablet: '10',
          desktop: '10',
        },
        deviceType: 'desktop',
      },
      ...paddingProps,
      ...borderProps,
      customCheckboxText:
        '<p style="line-height: 1.5;"><span style="font-size: 16px; color: rgb(107, 114, 128);">Billing Address same as Shipping Address</span></p>',
      customCheckbox: {
        checkboxColor: '#4f46e5',
        checkedBorderColor: '#4f46e5',
        uncheckedBorderColor: '#d1d5db',
        checkboxshape: 'rounded',
        checkboxSize: '16',
        enrollmentStyle: 'checkbox',
        checkMarkColor: '#ffffff',
        checkedToggleColor: '#4f46e5',
        uncheckedToggleColor: '#d1d5db',
        toggleSize: '20',
        checkMarkSize: '16',
      },
      option: {
        FirstName: {
          header: {
            text: 'First Name',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'First Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          disableRequiredValidation: false,
          requiredValidation: true,
          validationText: 'First Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        LastName: {
          header: {
            text: 'Last Name',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Last Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          disableRequiredValidation: false,
          validationText: 'Last Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Address1: {
          header: {
            text: 'Address',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          restrictMilitaryAddress: false,
          restrictPOBox: false,
          placeholder: 'Address',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          disableRequiredValidation: true,
          requiredValidation: true,
          validationText: 'Address is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Address2: {
          header: {
            text: 'Address 2',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Apartment, suite, etc. (optional)',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
            ...paddingProps,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
            ...paddingProps,
          },
          ...defaultBorderProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        City: {
          header: {
            text: 'City',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'City',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          disableRequiredValidation: true,
          validationText: 'City is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        CountryCode: {
          header: {
            text: 'Country',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Select',
          show: true,
          showOnlyCountry: [],
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          disableRequiredValidation: true,
          validationText: 'Country is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        ProvinceCode: {
          header: {
            text: 'State / Province',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Select State / Province',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          disableRequiredValidation: false,
          requiredValidation: true,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Organization: {
          header: {
            text: 'Company',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Company (optional)',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Zip: {
          header: {
            text: 'Postal Code',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Postal code',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          disableRequiredValidation: true,
          requiredValidation: true,
          validationText: 'Postal code is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Phone: {
          header: {
            text: 'Phone',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Phone',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Phone number is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
      },
      isShowBillingAddress: false,
      billinForm: {
        FirstName: {
          header: {
            text: 'First Name',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'First Name',
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          show: true,
          requiredValidation: true,
          validationText: 'First Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        LastName: {
          header: {
            text: 'Last Name',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Last Name',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Last Name is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Address1: {
          header: {
            text: 'Address',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Address',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Address is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Address2: {
          header: {
            text: 'Address 2',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Apartment, suite, etc. (optional)',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        City: {
          header: {
            text: 'City',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'City',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'City is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        CountryCode: {
          header: {
            text: 'Country',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Select',
          showOnlyCountry: [],
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Country is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        ProvinceCode: {
          header: {
            text: 'State / Province',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Select State / Province',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Organization: {
          header: {
            text: 'Company',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Company (optional)',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: false,
          validationText: '',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Zip: {
          header: {
            text: 'Postal Code',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Postal code',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Postal code is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
        Phone: {
          header: {
            text: 'Phone',
            hide: false,
            ...paddingProps,
            ...marginProps,
          },
          placeholder: 'Phone',
          show: true,
          textColor: {
            color: '#000000',
            isInherit: false,
          },
          textBackgroundColor: {
            color: '#ffffff',
            isInherit: false,
          },
          ...defaultBorderProps,
          requiredValidation: true,
          validationText: 'Phone number is required',
          color: '#808080',
          fontSize: '14',
          fontWeight: '300',
          lineHeight: '1.5',
          textAlign: 'left',
          fontFamily: 'sans-serif',
          letterSpace: '0',
          ...outlineProps,
        },
      },
      maintainRows: {
        deviceType: 'desktop',
        countryProvince: {
          desktop: false,
          tablet: false,
          mobile: true,
        },
        firstLastName: {
          desktop: false,
          tablet: false,
          mobile: true,
        },
      },
    },
    render: ({
      margin,
      option,
      isShowBillingAddress,
      editMode,
      heightField,
      padding,
      border,
      billinForm,
      customCheckboxText,
      customCheckbox,
      maintainRows,
    }) => {
      const dispatch = useAppDispatch();
      const {
        watch,
        setValue,
        formState: { errors, dirtyFields },
      } = useFormContext<FormData>();

      const isShppingDirty = !!dirtyFields?.Shipping;
      const { cartData } = useAppSelector(state => state.cart);
      const { pageId } = useAppSelector(state => state.checkout);
      const storeData = useAppSelector(state => state.store.storeData);
      const windowWidth = useAppSelector(state => state.store.currentWidth);
      const isCartApiLoading = useAppSelector(
        state => state.cart.isCartApiLoading,
      );
      const currentDeviceType = getcurrentDeviceType(windowWidth, editMode);
      const publish = EventPublisher.getInstance().publish;
      const marginStyles = getMarginStyles(margin, currentDeviceType);
      const state = useAppSelector(state => state);
      const [check, setCheck] = useState(true);

      const {
        Address1,
        Address2,
        City,
        CountryCode,
        FirstName,
        LastName,
        Organization,
        ProvinceCode,
        Zip,
      } = watch('Shipping');

      const { Email, PhoneNumber } = watch('CustomerData');

      // Sync shipping data to global store for Funnelish component
      useEffect(() => {
        const shippingData = {
          Address1: Address1 || '',
          Address2: Address2 || '',
          City: City || '',
          CountryCode: CountryCode || '',
          FirstName: FirstName || '',
          LastName: LastName || '',
          Organization: Organization || '',
          ProvinceCode: ProvinceCode || '',
          Zip: Zip || '',
          Email: Email || '',
          PhoneNumber: PhoneNumber || '',
        };
        if (window.setGlobalShippingData && isCartApiLoading) {
          window.setGlobalShippingData(shippingData);

          const hasShippingChanges =
            Address1 ||
            Address2 ||
            City ||
            CountryCode ||
            FirstName ||
            LastName ||
            Organization ||
            ProvinceCode ||
            Zip;
          const hasContactChanges = Email || PhoneNumber;

          if (
            hasShippingChanges &&
            cartData?.ID &&
            storeData?.ShopID &&
            storeData?.StoreID &&
            !isCartApiLoading
          ) {
            updateShippingDetailsDebounce();
          }

          if (
            hasContactChanges &&
            cartData?.ID &&
            storeData?.ShopID &&
            storeData?.StoreID
          ) {
            updateContactInfoDebounce();
          }
        }
      }, [
        Address1,
        Address2,
        City,
        CountryCode,
        FirstName,
        LastName,
        Organization,
        ProvinceCode,
        Zip,
        Email,
        PhoneNumber,
        isCartApiLoading,
        cartData?.ID,
        storeData?.ShopID,
        storeData?.StoreID,
      ]);

      const PhoneCode = watch('phone_code');

      const isShippingAddressValid = () => {
        // const ProvinceList = getStatesForCountry(countriesList, CountryCode);
        const isCommonFieldsValid =
          !!FirstName &&
          !!LastName &&
          !!Address1 &&
          !!City &&
          !!CountryCode &&
          !!Zip &&
          !errors.Shipping?.FirstName &&
          !errors.Shipping?.LastName &&
          !errors.Shipping?.Address1 &&
          !errors.Shipping?.City &&
          !errors.Shipping?.CountryCode &&
          !errors.Shipping?.Zip;

        // const isProvinceValid = !ProvinceList?.provinces?.length
        //   ? true
        //   : !!ProvinceCode && !errors.Shipping?.ProvinceCode;

        return isCommonFieldsValid;
      };

      const updateShippingDetails = useCallback(async () => {
        if (
          isShippingAddressValid() &&
          cartData?.ID &&
          storeData?.ShopID &&
          storeData?.StoreID
        ) {
          const payload = {
            Address: {
              Address1,
              Address2,
              City,
              CountryCode,
              FirstName,
              LastName,
              Organization,
              ProvinceCode,
              Zip,
            },
            ID: cartData.ID,
            ShopID: storeData.ShopID,
            StoreID: Number(storeData.StoreID),
            PageID: String(pageId) || '',
          };

          dispatch(setApiLoading(true));
          try {
            const uniqueEventId = uuidv4();
            const res = await checkoutService.setShippingAddress(
              payload,
              uniqueEventId,
            );
            dispatch(setIsSetShippingApiLoading((prev: number) => prev + 1));
            if (res?.data?.Cart) {
              if (!getIsViewIsEdit()) {
                publish('ShippingAdded', {
                  eventName: 'ShippingAdded',
                  eventId: uniqueEventId,
                  shopId: storeData.ShopID,
                  cart: res?.data?.Cart,
                });
              }
              dispatch(
                setAvailableShippingMethods(
                  res?.data?.Cart?.AvailableShippingMethods as ShippingMethod[],
                ),
              );
              dispatch(
                setSalesTax({
                  SalesTaxAmount: res?.data?.Cart?.SalesTaxAmount,
                  TotalAmount: res?.data?.Cart?.TotalAmount,
                  EstimatedTaxAmount: res?.data?.Cart?.EstimatedTaxAmount,
                  Fees: res?.data?.Cart?.Fees,
                  DiscountAmount: res?.data?.Cart?.DiscountAmount,
                  SubtotalAmount: res?.data?.Cart?.SubtotalAmount,
                  ShippingRequired: res?.data?.Cart?.ShippingRequired,
                  ShippingMethodID: res?.data?.Cart?.ShippingMethodID,
                  TicketEntries: res?.data?.Cart?.TicketEntries,
                }),
              );
            }
          } catch (error: unknown) {
            if (error instanceof Error) {
              dispatch(setCheckoutErrorMessage(error.message));
              return;
            }
            dispatch(setCheckoutErrorMessage('An unknown error occurred'));
          } finally {
            dispatch(setIsSetShippingApiLoading((prev: number) => prev - 1));
          }

          dispatch(setApiLoading(false));
        }
      }, [
        dispatch,
        cartData?.ID,
        storeData?.ShopID,
        storeData?.StoreID,
        Address1,
        Address2,
        City,
        CountryCode,
        FirstName,
        LastName,
        Organization,
        ProvinceCode,
        Zip,
        Email,
        PhoneNumber,
        PhoneCode,
      ]);

      const updateContactInfo = useCallback(async () => {
        if (
          Email &&
          PhoneNumber &&
          cartData?.ID &&
          storeData?.ShopID &&
          storeData?.StoreID
        ) {
          const payload = {
            ID: cartData.ID,
            ShopID: storeData.ShopID,
            StoreID: Number(storeData.StoreID),
            Email: Email,
            PhoneNumber: `${PhoneCode} ${PhoneNumber}`,
            PageID: String(pageId) || '',
          };

          try {
            await checkoutService.setContactInfo(payload);
          } catch (error: unknown) {
            console.error('Error updating contact info:', error);
            if (error instanceof Error) {
              dispatch(setCheckoutErrorMessage(error.message));
              return;
            }
            dispatch(setCheckoutErrorMessage('An unknown error occurred'));
          }
        }
      }, [
        dispatch,
        cartData?.ID,
        storeData?.ShopID,
        storeData?.StoreID,
        Email,
        PhoneNumber,
        PhoneCode,
        pageId,
      ]);

      const updateShippingDetailsDebounce = useDebounce(
        updateShippingDetails,
        500,
      );

      const updateContactInfoDebounce = useDebounce(updateContactInfo, 500);

      useEffect(() => {
        if (isShppingDirty) updateShippingDetailsDebounce();
        dispatch(setIsValidShippingForm(isShippingAddressValid()));
      }, [
        isShippingAddressValid(),
        FirstName,
        LastName,
        Address1,
        Address2,
        Organization,
        City,
        CountryCode,
        ProvinceCode,
        Zip,
      ]);

      if (
        (getIsViewIsEdit() &&
          option?.CountryCode?.defaultCountry &&
          option?.CountryCode?.defaultCountry?.length &&
          CountryCode !== option?.CountryCode?.defaultCountry) ||
        (CountryCode.length === 0 &&
          option?.CountryCode?.defaultCountry &&
          option?.CountryCode?.defaultCountry?.length > 0)
      ) {
        setValue('Shipping.CountryCode', option?.CountryCode?.defaultCountry);
      }

      useEffect(() => {
        setValue('phone_code', getPhoneCode() || '+1');
      }, [setValue]);
      return (
        <div
          className={`${border?.borderStyle?.[currentDeviceType]}`}
          style={{
            ...marginStyles,
            ...getPaddingStyles(padding, currentDeviceType),
            ...getBorderProps(border, currentDeviceType),
          }}>
          <AddressForm
            key="shipping-address-form"
            fieldPrefix="Shipping"
            useAppSelector={useAppSelector}
            option={option}
            storeData={storeData}
            cartData={cartData}
            maintainRows={maintainRows}
            fieldHeight={heightField?.height?.[currentDeviceType]}
            rowGap={heightField?.rowGap?.[currentDeviceType]}
            colGap={heightField?.colGap?.[currentDeviceType]}
            currentDeviceType={currentDeviceType}
          />
          {isShowBillingAddress && (
            <section
              aria-labelledby="billing-heading"
              className="col-span-full pt-4">
              <div className="checkbox-control">
                <label className="flex items-center">
                  <Controller
                    name="billing_same_as_shipping"
                    render={({ field: { value, onChange } }) =>
                      customCheckbox?.enrollmentStyle === 'checkbox' ? (
                        <input
                          type="checkbox"
                          onChange={e => onChange(e.target.checked)}
                          checked={value}
                          className={`${customCheckbox?.checkboxshape} check-box-billing focus:ring-0 focus:ring-offset-0 h-4 w-4 border-gray-300 cursor-pointer`}
                          style={{
                            color: customCheckbox?.checkboxColor,
                            width: `${customCheckbox?.checkboxSize}px`,
                            height: `${customCheckbox?.checkboxSize}px`,
                            borderColor: value
                              ? customCheckbox?.checkedBorderColor
                              : customCheckbox?.uncheckedBorderColor,
                          }}
                        />
                      ) : (
                        <Toggle
                          checked={check}
                          style={{
                            backgroundColor: check
                              ? customCheckbox?.checkedToggleColor
                              : customCheckbox?.uncheckedToggleColor,
                            color: customCheckbox?.checkMarkColor,
                            width: `${customCheckbox?.toggleSize}px`,
                            height: `${customCheckbox?.toggleSize}px`,
                          }}
                          circleWidth={`${customCheckbox?.checkMarkSize}px`}
                          circleHeight={`${customCheckbox?.checkMarkSize}px`}
                          handleToggle={() => {
                            setCheck(!check);
                            onChange(!check);
                          }}
                        />
                      )
                    }
                  />
                  <div
                    className="ml-2 check-box-same-shipping-address-text cursor-pointer checkbox-content"
                    dangerouslySetInnerHTML={{
                      __html: getQuillEditorText(customCheckboxText, state),
                    }}
                  />
                </label>
              </div>
            </section>
          )}
          {!watch('billing_same_as_shipping') && isShowBillingAddress && (
            <div className="mt-4">
              <h1 className="font-semibold text-black text-lg mb-4">
                Billing address
              </h1>
              <AddressForm
                key="payment-billing-address"
                fieldPrefix="BillingAddress"
                fieldHeight={heightField?.height?.[currentDeviceType]}
                storeData={storeData}
                cartData={cartData}
                rowGap={heightField?.rowGap?.[currentDeviceType]}
                currentDeviceType={currentDeviceType}
                colGap={heightField?.colGap?.[currentDeviceType]}
                useAppSelector={useAppSelector}
                option={billinForm}
              />
            </div>
          )}
        </div>
      );
    },
  };
};
